'use client';
import { useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import { Fade } from '@mui/material';

import { tss } from 'tss-react/mui';

import { useQuery } from '@apollo/client/react';

import { graphql } from '@/gql';

import { InfiniteScroll } from '@/components/infinite-scroll/InfiniteScroll';
import { NewsArticle } from '@/modules/news/components/NewsArticle';
import {
  extractConnectionProps,
  extractPaginationProps,
} from '@/utils/queries/queryExtracts';

const NEWS_ARTICLE_QUERY = graphql(`
  query NewsArticlesQuery($first: Int, $after: String) {
    currentUser {
      _id
      hasGovernorHubKnowledge
    }
    newsArticleConnection(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          _id
          title
          date
          url
          link
          type
          description
          favicon_url
          provider_name
          provider_url
          displayImages {
            newsfeed
          }
          knowledgeSuggestionsConnection {
            edges {
              node {
                title
                url
              }
            }
          }
          feed {
            _id
            name
            type
            owner {
              ... on Group {
                _id
                name
                machineName
                iconUrl
              }
              ... on Service {
                _id
                name
                machineName
                iconUrl
              }
            }
            source
          }
          lastModifiedBy {
            _id
            profileImage
            firstName
            lastName
            ...userImageFields
          }
          lastModified
        }
      }
    }
  }
`);

const useStyles = tss.create(({ theme }) => {
  return {
    root: { width: '100%' },
    article: {
      paddingBottom: theme.spacing(2),
    },
    loaderRoot: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: theme.spacing(3),
    },
  };
});

export const Newsfeed = () => {
  const { classes } = useStyles();

  const [fetchOutstanding, setFetchOutstanding] = useState(false);

  const { data, loading, fetchMore } = useQuery(NEWS_ARTICLE_QUERY, {
    variables: { first: 10, after: null },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const { endCursor, hasNextPage } = extractPaginationProps(
    data?.newsArticleConnection,
  );

  const newsArticles = extractConnectionProps(data?.newsArticleConnection);

  const hasKnowledge = Boolean(data?.currentUser?.hasGovernorHubKnowledge);

  const hasMoreArticles = Boolean(hasNextPage);

  return (
    <div className={classes.root}>
      <InfiniteScroll
        hasMore={hasMoreArticles}
        isLoading={loading}
        load={async () => {
          if (
            fetchOutstanding ||
            !(data && data.newsArticleConnection) ||
            !hasMoreArticles
          ) {
            return;
          }

          setFetchOutstanding(true);
          await fetchMore({
            variables: { first: 10, after: endCursor },
          });
          setFetchOutstanding(false);
        }}
        loader={
          <div className={classes.loaderRoot} key={0}>
            <CircularProgress disableShrink />
          </div>
        }
      >
        {newsArticles.map((article) => {
          return (
            <Fade in={true} key={article._id || undefined} timeout={200}>
              <div className={classes.article}>
                <NewsArticle article={article} hasKnowledge={hasKnowledge} />
              </div>
            </Fade>
          );
        })}
      </InfiniteScroll>
    </div>
  );
};
